import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: string | null;
  profile: any | null;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any, userId?: string, shouldSwitchToSignIn?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth event:', event, 'Session:', !!session);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fetch role and profile without async in callback
          setTimeout(() => {
            fetchUserRole(session.user.id);
            fetchUserProfile(session.user.id);
            setLoading(false);
          }, 0);
        } else {
          setUserRole(null);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session:', !!session);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
        fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user role:', error);
        return;
      }
      
      setUserRole(data?.role || null);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user profile:', error);
        return;
      }
      
      setProfile(data || null);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });

    if (error) {
      let errorMessage = error.message;
      let shouldSwitchToSignIn = false;
      
      if (error.message.includes('User already registered') || 
          error.message.includes('duplicate') || 
          error.message.includes('already exists') ||
          error.message.includes('users_email_partial_key')) {
        errorMessage = "Cet email existe déjà dans le système. Utilisez l'onglet 'Sign In' pour vous connecter.";
        shouldSwitchToSignIn = true;
      }
      
      toast({
        variant: "destructive",
        title: "Erreur d'inscription",
        description: errorMessage
      });
      return { error, shouldSwitchToSignIn };
    }

    // Create profile for the new user
    if (data.user && !error) {
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([{
            user_id: data.user.id,
            full_name: fullName
          }]);

        if (profileError) {
          console.error('Error creating profile:', profileError);
          toast({
            variant: "destructive",
            title: "Erreur d'inscription", 
            description: "Erreur de base de données lors de la sauvegarde du nouvel utilisateur"
          });
          return { error: profileError, shouldSwitchToSignIn: false };
        }
      } catch (profileError) {
        console.error('Error creating profile:', profileError);
        toast({
          variant: "destructive",
          title: "Erreur d'inscription",
          description: "Erreur de base de données lors de la sauvegarde du nouvel utilisateur"  
        });
        return { error: profileError, shouldSwitchToSignIn: false };
      }
    }

    toast({
      title: "Inscription réussie",
      description: "Veuillez vérifier votre email pour confirmer votre compte."
    });

    return { error, userId: data.user?.id, shouldSwitchToSignIn: false };
  };

  const signIn = async (email: string, password: string) => {
    console.log('Attempting sign in for:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('Sign in error:', error);
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: error.message
      });
      return { error };
    }

    console.log('Sign in successful, user:', data.user?.id);
    
    // Wait for role to be fetched
    if (data.user) {
      await fetchUserRole(data.user.id);
      await fetchUserProfile(data.user.id);
    }

    toast({
      title: "Bienvenue !",
      description: "Vous êtes connecté avec succès."
    });

    return { error: null };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Erreur de déconnexion",
        description: error.message
      });
    } else {
      toast({
        title: "Déconnecté",
        description: "Vous avez été déconnecté avec succès."
      });
    }
  };

  const value = {
    user,
    session,
    userRole,
    profile,
    signUp,
    signIn,
    signOut,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};